import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Typography, Divider, Link } from "@castlery/fortress";
import { ChevronDown, ChevronUp } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorContent } from "./Message";

interface ShipmentItem {
  itemId: string;
  itemName: string;
  quantity: number;
}

interface ShipmentData {
  documentNumber: string;
  lastMileStatus: string;
  scheduledDeliveryDate: string;
  scheduledDeliverySlotStartTime?: string;
  scheduledDeliverySlotEndTime?: string;
  lines: ShipmentItem[];
}

// 单个发货卡片组件
const ShipmentCardItem: React.FC<{ item: ShipmentData }> = ({ item }) => {
  const { documentNumber, lastMileStatus, scheduledDeliveryDate, lines } = item;

  return (
    <div className="p-4 space-y-3 bg-[#F6F3E7]">
      {/* 第一行：SHIPMENT NO 和 STATUS */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Typography level="subh3" className="text-[#616161]">
            SHIPMENT NO
          </Typography>
          <Typography level="caption1">{documentNumber}</Typography>
        </div>
        <div>
          <Typography level="subh3" className="text-[#616161]">
            STATUS
          </Typography>
          <Typography level="caption1" style={{ textTransform: "capitalize" }}>
            {lastMileStatus
              ? lastMileStatus.toLowerCase().replace(/_/g, " ")
              : ""}
          </Typography>
        </div>
      </div>

      {/* 第二行：ESTIMATED DELIVERY DATE */}
      {scheduledDeliveryDate && (
        <div>
          <Typography level="subh3" className="text-[#616161]">
            ESTIMATED DELIVERY DATE
          </Typography>
          <Typography level="caption1">{`${formatDateTime(
            scheduledDeliveryDate,
            "DD MMM YYYY"
          )}`}</Typography>
        </div>
      )}

      {/* 分隔线 */}
      <Divider className="m-3"></Divider>

      {/* 商品列表 */}
      <div>
        <Typography level="subh3" className="text-[#616161]">
          ITEM
        </Typography>
        <div className="flex flex-col gap-2">
          {lines.map((item, index) => (
            <div key={index} className="flex justify-between gap-2 font-aime">
              <Typography level="caption1" style={{ wordBreak: "break-word" }}>
                {item.itemName || item.itemId}
              </Typography>
              <Typography level="caption1" className="flex-shrink-0">
                x {item.quantity}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ShipmentCardWithoutErrorBoundary = (props: {
  data: { list: ShipmentData[] };
}) => {
  const { list = [] } = props.data;

  const [isExpanded, setIsExpanded] = useState(false);

  const maxVisibleItems = 3;
  const hasMoreItems = list.length > maxVisibleItems;
  const visibleItems = isExpanded ? list : list.slice(0, maxVisibleItems);

  if (list.length === 0) {
    return null;
  }

  return (
    <Card className="w-full rounded-md rounded-tl-none border border-[#F6F3E7] bg-[#FBF9F4] text-[#3C101E]">
      <CardContent className="p-3 flex flex-col gap-3 font-aime">
        {visibleItems.map((item, index) => (
          <ShipmentCardItem
            key={`shipment-${item.documentNumber}-${index}`}
            item={item}
          />
        ))}

        {/* View more/less 按钮 */}
        {hasMoreItems && (
          <div className="flex items-center justify-center">
            <Link
              variant="secondary"
              size="xs"
              endDecorator={
                isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )
              }
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "View less shipment" : "View all shipment"}
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const ShipmentCard = (props: { data: { list: ShipmentData[] } }) => {
  return (
    <ErrorBoundary fallback={<ErrorContent position="left" />}>
      <ShipmentCardWithoutErrorBoundary {...props} />
    </ErrorBoundary>
  );
};
